import { PlexUserRelationState, TaskUserRelationState } from '@all-hands-hq/plex-graphql-schema';
import MomentUtils from '@date-io/moment';
import { createStyles, FormControl, makeStyles, Tooltip } from '@material-ui/core';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import classNames from 'classnames';
import Moment from 'moment-timezone';
import React, { useState } from 'react';

import { useUpdateTaskMutation } from '../../../../graphql/gen/types';
import { spacings } from '../../../../theme';
import { canEditTask } from '../../permissions';
import { TaskField } from './common/TaskField';

export const TaskDueDateView = ({
  className,
  dueDate,
}: {
  className?: string;
  dueDate: Date | null;
}): React.ReactElement => {
  const classes = useStyles();

  const component = (
    <span className={classNames(className, classes.date, { [classes.nonEmptyDate]: dueDate != null })}>
      {dueDate != null ? Moment(dueDate).format('MM/DD/YYYY') : 'None'}
    </span>
  );

  if (dueDate != null) {
    return <Tooltip title={Moment(dueDate).format('dddd, DD MMM YYYY')}>{component}</Tooltip>;
  } else {
    return component;
  }
};

type Props = {
  id: string;
  dueDate: Date | null;
  plexRelation: PlexUserRelationState;
  taskRelation: TaskUserRelationState;
};

type State =
  | { type: 'initial' }
  | {
      type: 'editing';
      value: Date | null;
    };

export const TaskDueDate = ({ id, plexRelation, taskRelation, dueDate }: Props): React.ReactElement => {
  const classes = useStyles();
  const [state, setState] = useState<State>({ type: 'initial' });
  const [updateTask, { loading }] = useUpdateTaskMutation();

  return (
    <div className={classes.root}>
      {state.type === 'initial' && (
        <TaskField
          mode="view"
          label="Due Date"
          onClick={
            canEditTask({ plexRelation, taskRelation })
              ? () => {
                  setState({ type: 'editing', value: dueDate });
                }
              : null
          }
        >
          <TaskDueDateView className={classes.dueDateContainer} dueDate={dueDate} />
        </TaskField>
      )}

      {state.type === 'editing' && (
        <TaskField
          mode="edit"
          label="Due Date"
          saving={loading}
          onClickAway={() => {
            if (dueDate !== state.value) {
              updateTask({ variables: { task: { id, dueDate: state.value } } }).then(() => {
                setState({ type: 'initial' });
              });
            } else {
              setState({ type: 'initial' });
            }
          }}
        >
          <FormControl size="small">
            <MuiPickersUtilsProvider utils={MomentUtils}>
              <KeyboardDatePicker
                autoOk
                disabled={loading}
                variant="inline"
                format="MM/DD/YYYY"
                margin="dense"
                onAccept={(date) => {
                  if (date != null) {
                    updateTask({ variables: { task: { id, dueDate: date.toDate() } } }).then(() => {
                      setState({ type: 'initial' });
                    });
                  }
                }}
                value={state.value}
                onChange={(date) => {
                  if (date != null) {
                    setState({ ...state, value: date.toDate() });
                  } else {
                    setState({ ...state, value: null });
                  }
                }}
                KeyboardButtonProps={{
                  'aria-label': 'change due date',
                }}
              />
            </MuiPickersUtilsProvider>
          </FormControl>
        </TaskField>
      )}
    </div>
  );
};

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      display: 'inline-flex',
      alignItems: 'center',
      height: '44px',
    },
    label: {
      marginRight: spacings.small,
    },
    dueDateContainer: {
      display: 'inline-flex',
      alignItems: 'center',
    },
    date: {
      fontSize: 15,
    },
    nonEmptyDate: {
      fontWeight: 500,
    },
    inlineButton: {
      display: 'inline',
      marginLeft: spacings.small,
    },
  })
);
